import {SpinnerComponent} from "./SpinnerComponent";
import {Component} from "substance";
import clone from "lodash/clone";
import {debounce} from '../../../../lib/utils/debounce'

/**
 * @typedef UIFormSearch.props
 * @property {string} placeholderText - Placeholder text in the field
 * @property {boolean} [createAllowed=false] - Set to true to allow creation of new items
 * @property {boolean} [focusOnRender=false] - Whether or not to focus on the input field after render. Default is false
 * @property {function} onSelect - Triggers when item is selected
 * @property {function} [onCreate] - If possible to create new items, pass this callback
 * @property {UIFormSearch.Item[]} existingItems
 */

/**
 * @typedef UIFormSearch.Item
 * @property {string} name
 * @property {string} value
 * @property {string} uuid
 * @property {string} imType
 * @property {string} shortDescription
 */

/**
 * @typedef UIFormSearch.state
 * @property {UIFormSearch.Item[]} [items=[]]
 * @property {Boolean} [isSearching=false]
 * @property {number} [currentSelectedIndex=0]
 */

/**
 * A form search component
 *
 * @name UIFormSearch
 * @class
 *
 * @example
 *
 * import {UIFormSearch} from 'writer'
 *
 * render($$) {
 *     return $$(UIFormSearch, {
 *         existingItems: [{name: 'Test', value: 'test', uuid: '13c4d7f6-4ecb-4d93-905c-3f5752a04f8d', imType: 'x-im/author', shortDescription: 'My short description'}],
 *         onSelect: (item) => {
 *             console.info(item)
 *         },
 *         onCreate: (addedItem, doesItemAlreadyExist) => {
 *             console.info(addedItem, doesItemAlreadyExist)
 *         },
 *         createAllowed: true,
 *         placeholderText: 'Enter text'
 *     })
 * }
 *
 * @param {UIFormSearch.props} props
 * @property {UIFormSearch.props} props
 * @property {UIFormSearch.state} state
 */

class FormSearchComponent extends Component {

    constructor(...args) {
        super(...args)

        if (this.props.createAllowed && !this.props.onCreate) {
            console.warn("Creation of items is allowed but onCreate method is missing");
        }

        this.searchFunction = debounce(() => this.lookup(), 300);
    }

    next() {
        const selectedDomItem = document.getElementById('item-' + this.currentSelectedItem.uuid)
        let currentSelectedItem = this.currentSelectedItem && selectedDomItem
        if (currentSelectedItem) {
            let searchResultElement = this.refs.searchResult.el.el;
            let selectedItem = selectedDomItem
            let items = this.state.items

            if (this.state.currentSelectedIndex >= 0) {
                let idx = parseInt(this.state.currentSelectedIndex, 10)
                idx++;

                searchResultElement.scrollTop = selectedItem.offsetTop;
                this.extendState({
                    currentSelectedIndex: idx
                });

                if (idx === items.length) {
                    this.extendState({
                        currentSelectedIndex: 0
                    });
                    searchResultElement.scrollTop = 0
                }
            }
        }
    }

    prev() {
        const selectedDomItem = document.getElementById('item-' + this.currentSelectedItem.uuid)
        let currentSelectedItem = this.currentSelectedItem && selectedDomItem
        if (currentSelectedItem) {
            let searchResultElement = this.refs.searchResult.el.el
            let selectedItemPrevSibling = selectedDomItem.previousSibling

            let items = this.state.items;
            if (this.state.currentSelectedIndex >= 0) {
                var idx = parseInt(this.state.currentSelectedIndex, 10)

                if (selectedItemPrevSibling === null) {
                    searchResultElement.scrollTop = searchResultElement.lastChild.offsetTop
                    this.extendState({
                        currentSelectedIndex: items.length - 1
                    });

                } else {
                    let offsetTop = selectedItemPrevSibling.offsetTop
                    let offsetHeight = selectedItemPrevSibling.offsetHeight

                    searchResultElement.scrollTop = offsetTop - offsetHeight
                    idx--;

                    this.extendState({
                        currentSelectedIndex: idx
                    });
                }
            }
        }
    }

    search(e) {
        switch (e.keyCode) {

            case 38: // up arrow
                if (e.shiftKey) return;
                e.preventDefault();
                this.prev();
                break;

            case 40: // down arrow
                if (e.shiftKey) return;
                e.preventDefault();
                this.next();
                break;

            case 9: // tab
            case 13: // enter
                e.preventDefault();
                e.stopPropagation();
                if (this.state.items.length === 0) return;
                this.select();
                break;

            case 27: // escape
                this.hide();
                break;
            default:
                this.searchFunction()
        }
    }

    clearItems() {
        this.extendState({
            items: [],
            isSearching: false,
            currentSelectedIndex: 0
        })
    }

    hide(e) {
        this.refs.searchInput.val("");
        this.clearItems()
    }

    select() {
        this.doAction(this.currentSelectedItem);
    }

    lookup() {
        var input = this.refs.searchInput.val().trim(),
            originalInput = input;

        if (input.length === 0) {
            this.clearItems()
            return;
        }

        this.extendState({
            isSearching: true
        });

        // Might pass a boolean from parent to search with wildcard?
        var wildcardSearch = true;
        if (wildcardSearch) {
            input += '*';
        }

        if (this.props.searchUrl === null) {
            this.handleSearchResult([]);
        } else if (this.props.conceptService) {
            const { conceptService } = this.props
            conceptService.searchForConceptSuggestions('x-im/author', input)
                .then(result => {
                    if (originalInput === this.refs.searchInput.val().trim()) {
                        // Search should be handled, it is the same input as when the request was sent
                        this.handleSearchResult(result)
                    } else {
                        // input differs from search request, skip it.
                        this.extendState({
                            isSearching: false
                        });
                    }
                })
        }
        else {
            console.warn('formSearch through ConceptBackend (searchUrl) is deprecated, use ConceptService instead')

            const fetchParameters = {
                headers: {
                    contentType: 'application/json',
                    dataType: 'application/json'
                }
            }

            this.context.api.router.get(this.props.searchUrl + input, fetchParameters)
                .then(response => this.context.api.router.checkForOKStatus(response))
                .then(response => this.context.api.router.toJson(response))
                .then((json) => {
                    if (originalInput === this.refs.searchInput.val().trim()) {
                        // Search should be handled, it is the same input as when the request was sent
                        this.handleSearchResult(json);
                    } else {
                        // input differs from search request, skip it.
                        this.extendState({
                            isSearching: false
                        });
                    }
                })
                .catch((e) => {
                    console.error(e)
                    this.extendState({
                        isSearching: false
                    });
                })
        }
    }

    handleSearchResult(data) {
        this.extendState({
            items: this.getItemsThatNotExisting(data),
            isSearching: false
        });
    }

    getInitialState() {
        return {
            items: [],
            currentSelectedIndex: 0
        };
    }

    /**
     * Adds a dummy element to items list which contains a "create item" element
     * @private
     * @returns {{name: string[], uuid: string, shortDescription: string[]}}
     */
    getCreateNewItem() {
        return {
            name: [this.getLabel("Create") + ": " + this.refs.searchInput.val().trim()],
            value: this.refs.searchInput.val().trim(),
            uuid: "__create-new",
            imType: ["x-im/template"],
            shortDescription: [
                ""
            ]
        };
    }

    getItemsThatNotExisting(data) {
        var existingItems = this.props.existingItems;

        if (this.props.onCreate && this.props.createAllowed) {
            data.push(this.getCreateNewItem());
        }

        var existingItemsMap = {};
        if (existingItems) {
            existingItems.forEach(function (item) {
                existingItemsMap[item.uuid] = item.uuid;
            });
        }

        data.forEach(function (item) {
            if (existingItemsMap[item.uuid]) {
                item.exists = true;
            }
        });

        return data;
    }


    itemAlreadyExists(items, item) {
        if (!items) {
            return false;
        }

        for (var i = 0; i < items.length; i++) {
            let value = items[i].ConceptName || items[i].title || items[i].name[0]
            let newValue = item.ConceptName || item.value || item.name[0]
            if (item.uuid !== '__create-new' && value.toLowerCase() === newValue.toLowerCase()) {
                return true;
            }
        }
        return false;
    }

    doAction(item) {
        if (item.exists || this.itemAlreadyExists(this.props.existingItems, item)) {
            this.context.api.ui.showNotification('formsearchcomponent', this.getLabel('formsearch.item-exists-label'), this.getLabel('formsearch.item-exists-description'))
            return;
        }
        if (this.props.onCreate && this.props.createAllowed && item.uuid === '__create-new') {
            this.props.onCreate(item, this.itemAlreadyExists(this.state.items, item));
        } else {
            this.props.onSelect(item);
        }

        this.hide();
    }

    render($$) {

        var formGroup = $$('div').addClass('form-group').ref('formGroup');

        var searchInput = $$('input')
            .addClass('form-control')
            .addClass('form__search')
            .on('keydown', this.search)
            .attr('autocomplete', 'off')
            .attr({
                type: 'text', id: 'formSearch', placeholder: this.getLabel(this.props.placeholderText)
            })
            .ref('searchInput');

        var inlineIcon = $$(SpinnerComponent, {isSearching: this.state.isSearching});

        formGroup.append(inlineIcon);
        formGroup.append(searchInput);


        var el = $$('div').addClass('search__container').ref('searchContainer');
        var list = $$('ul').attr('id', 'searchResult').ref('searchResult');

        if (this.state.items.length > 0) {
            list.addClass('isSearching');
        }

        this.state.items.forEach(function (item, idx) {
            var itemToSave = clone(item);

            var name = item.name ? item.name[0] : item.ConceptName ? item.ConceptName : '',
                description = item.shortDescription ? item.shortDescription[0] :
                    item.ConceptDefinitionShort ? item.ConceptDefinitionShort : '',
                itemId = 'item-' + item.uuid;

            itemToSave.inputValue = this.refs.searchInput.val().trim();


            var itemEl = $$('li');
            if (item.exists === true) {
                itemEl.addClass('item__exists');
                itemEl.append($$('span').append("\u2713 ").addClass('item__found'));
            }
            itemEl.append($$('span').append(name).addClass('item__name'))
                .append($$('span').append(description).addClass('item__short-description'))
                .attr('id', itemId);

            itemEl.on('mousedown', (e) => {
                e.preventDefault()
            })

            itemEl.on('click', function () {
                this.doAction(itemToSave);
            }.bind(this));

            if (this.state.currentSelectedIndex === idx) {
                this.currentSelectedItem = itemToSave;
                itemEl.addClass('active');
            }

            list.append(itemEl);
        }.bind(this));

        el.append(formGroup);
        el.append(list);
        searchInput.on('blur', this.hide)

        return el;
    }

    didMount() {
        if(this.props.focusOnRender) {
            this.refs.searchInput.getNativeElement().focus()
        }
    }
}
export {FormSearchComponent}
